import React, { useState, useEffect, useRef } from "react";
import PerformanceChart from "./components/PerformanceChart";
import "./style.css"; // Importing the CSS file
import MainContentWrapper from "../../../Components/MainContentWrapper";
import Revenue from "./components/Revenue";
import RequestPlaced from "./components/RequestPlaced";
import SprayedAcres from "./components/SprayedAcres";
import DSPsOnboarded from "./components/DSPsOnboarded";
import LocationsAdded from "./components/LocationsAdded";
import Users from "./components/Users";
import BhumeetEnabledStores from "./components/BhumeetEnabledStores";
import SelectComponent from "../../../ReusableComponents/SelectComponent";
import OverViewGraph from "./components/overViewGraph";
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Button } from "../../../Components/Helper";
import Loading from "../../../ReusableFunctions/Loader";
import useWindowSize from "../../../hooks/useWindowSize";

const formatMonthYear = (dateString) =>
  new Date(dateString).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

const getMonthDifference = (fromDate, toDate) => {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  return (
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth()) +
    1
  );
};

const PeriodSelector = ({ filters, onFilterChange }) => {
  const [fromPeriods, setFromPeriods] = useState([]);
  const [toPeriods, setToPeriods] = useState([]);

  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const getLast12MonthsForFrom = () =>
    [...Array(12)]
      .map((_, i) => {
        const date = new Date(
          new Date().getFullYear(),
          new Date().getMonth() - i,
          1,
          0,
          0,
          0
        );
        return {
          value: date.toISOString(),
          label: `${date.toLocaleString("en-US", {
            month: "long",
          })} ${date.getFullYear()}`,
        };
      })
      .reverse();

  const getLast12MonthsForTo = () =>
    [...Array(12)]
      .map((_, i) => {
        const date = new Date(
          new Date().getFullYear(),
          new Date().getMonth() - (i - 1),
          0,
          23,
          59,
          59,
          999
        );
        return {
          value: date.toISOString(),
          label: `${date.toLocaleString("en-US", {
            month: "long",
          })} ${date.getFullYear()}`,
        };
      })
      .reverse();

  useEffect(() => {
    if (!filters) {
      const today = new Date();
      // Get first day of the month (6 months ago) at 00:00:00
      const fromDate = new Date(
        today.getFullYear(),
        today.getMonth() - 5,
        1,
        0,
        0,
        0,
        0
      );

      // Get last day of the current month at 23:59:59
      const toDate = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      setSelectedPeriod({
        periodType: "Month",
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
      });
      setFromPeriods(getLast12MonthsForFrom());
      setToPeriods(getLast12MonthsForTo());
    }
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      onFilterChange(selectedPeriod);
    }
  }, [selectedPeriod]);

  return (
    <>
      <div style={{ display: "flex", columnGap: "15px" }}>
        <div>
          <div style={{ fontSize: 12, margin: "0 0 2px 2px" }}>From</div>
          <SelectComponent
            controlStyle={{
              height: "30px",
              minHeight: "30px",
              background: "#ffffffd9",
              width: "150px",
              padding: "0px",
              border: "1px solid #ddd",
              boxShadow: "none",
              color: "hsl(0deg 0% 20% / 64%)",
              fontSize: "10px",
            }}
            isSearchable={false}
            menuStyles={{
              width: "150px",
            }}
            placeholder={"From"}
            menuPlacement={"bottom"}
            list={fromPeriods.filter(
              ({ value }) =>
                new Date(value) < new Date(selectedPeriod.to) &&
                new Date(value) <=
                  new Date(selectedPeriod.to).setMonth(
                    new Date(selectedPeriod.to).getMonth() - 5
                  )
            )}
            value={
              selectedPeriod
                ? {
                    label: `${formatMonthYear(selectedPeriod.from)}`,
                    value: selectedPeriod.from,
                  }
                : {}
            }
            optionStyle={{
              fontSize: "12px",
              height: "30px",
              minHeight: "30px",
            }}
            valueContainerStyle={{
              marginTop: "-2px",
            }}
            indicatorsContainerStyle={{
              marginTop: "-3px",
            }}
            onChange={(from) => {
              setSelectedPeriod({ ...selectedPeriod, from: from.value });
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, margin: "0 0 2px 2px" }}>To</div>
          <SelectComponent
            controlStyle={{
              height: "30px",
              minHeight: "30px",
              background: "#ffffffd9",
              width: "150px",
              padding: "0px",
              border: "1px solid #ddd",
              boxShadow: "none",
              color: "hsl(0deg 0% 20% / 64%)",
              fontSize: "10px",
            }}
            isSearchable={false}
            menuStyles={{
              width: "150px",
            }}
            placeholder={"To"}
            menuPlacement={"bottom"}
            list={toPeriods.filter(
              ({ value }) =>
                new Date(value) > new Date(selectedPeriod.from) &&
                new Date(value) >=
                  new Date(selectedPeriod.from).setMonth(
                    new Date(selectedPeriod.from).getMonth() + 5
                  )
            )}
            value={
              selectedPeriod
                ? {
                    label: `${formatMonthYear(selectedPeriod.to)}`,
                    value: selectedPeriod.to,
                  }
                : {}
            }
            optionStyle={{
              fontSize: "12px",
              height: "30px",
              minHeight: "30px",
            }}
            valueContainerStyle={{
              marginTop: "-2px",
            }}
            indicatorsContainerStyle={{
              marginTop: "-3px",
            }}
            onChange={(to) => {
              setSelectedPeriod({ ...selectedPeriod, to: to.value });
            }}
          />
        </div>
      </div>
    </>
  );
};

const BhuMeetPerformance = () => {
  const [labels, setLabels] = useState([]);
  const [filters, setFilters] = useState(null);
  const bhumeetPerformanceRef=useRef(null);
  const [isDownload, setIsDownload] = useState(false);
  const {currentWidth} =useWindowSize();


  const downloadPdf=()=>{
    setIsDownload(true);
    Loading.set(true);
    setTimeout(() => {
      const element=bhumeetPerformanceRef.current;
      html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
    }).then(canvas => {
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width; // Proportional image height
        let heightLeft = imgHeight;
  
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress:false,
        });
  
        let position = 0; // Position on the first page
        let pageCount = 1;
        pdf.setFontSize(10);
  
        // Add the first image
        const imgData = canvas.toDataURL('image/png', 1.0);
  
        pdf.addImage(
            imgData,
            'PNG',
            0,
            position,
            imgWidth,
            imgHeight,
            undefined,
             'FAST'
        );
        heightLeft -= pageHeight; // Subtract page height from image height
        pdf.text(`Page ${pageCount}`, 100, 290);
  
        // Add new pages if there's more content
        while (heightLeft > 0) {
            position = heightLeft - imgHeight; // Update position for next page
            pdf.addPage(); // Add new page
            pdf.addImage(
              imgData,
                'PNG',
                0,
                position,
                imgWidth,
                imgHeight 
            );
            pageCount++
            pdf.text(`Page ${pageCount}`, 100, 290);
            heightLeft -= pageHeight; // Subtract again for the next page
        }
  
        pdf.save('performanceOverView.pdf');
        setIsDownload(false);
        Loading.set(false);
    }); 
    }, 1000);
  }

  useEffect(() => {
    if (filters) {
      const months = Array.from(
        { length: getMonthDifference(filters.from, filters.to) },
        (_, i) => {
          const date = new Date(filters.from);
          date.setMonth(date.getMonth() + i);
          return date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
        }
      );
      setLabels(months);
    }
  }, [filters]);

  const onFilterChange = (changedFilters) => {
    setFilters({ ...filters, ...changedFilters });
  };

  return (
    <MainContentWrapper
      leftContent={"BhuMeet Peformance"}
      rightContent={
        <div className="flex  w-[30%] gap-6 justify-end items-center">
          <div className="mt-5">
          <Button
          style={{width:"120px",height:"30px"}}
            text={"Download Pdf"}
            type={"button"}
            onClick={downloadPdf}
            className={"btn-large "}
        />
          </div>
          <PeriodSelector onFilterChange={onFilterChange} />
         
        </div>
    }
    >
      <div ref={bhumeetPerformanceRef} className="flex flex-col gap-3">
        <div className="w-full ">
          <OverViewGraph isDownload={isDownload} labels={labels} commonFilters={filters} />
        </div>

        <div className="grid-container" style={{rowGap:isDownload ?currentWidth<1650 && currentWidth>1400?"140px":currentWidth<1400?"75px":currentWidth>1650?"60px":"20px":"20px"}}>
          <Revenue labels={labels} commonFilters={filters} />
          <RequestPlaced labels={labels} commonFilters={filters} />
          <SprayedAcres labels={labels} commonFilters={filters} />
          <DSPsOnboarded labels={labels} commonFilters={filters} />
          <LocationsAdded labels={labels} commonFilters={filters} />
          <Users labels={labels} commonFilters={filters} />
          <BhumeetEnabledStores labels={labels} commonFilters={filters} />
        </div>
      </div>
    </MainContentWrapper>
  );
};

export default BhuMeetPerformance;
